// @flow

import React from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import ZoomDuplicatedTokenImage from '../../../assets/images/zoom_token.png';

import { conversations } from '../../services/api/ava';
import { getSnack } from '../../utils/snackbar';

const intercomLinks = {
  en: 'https://intercom.help/ava/en/articles/3837352-where-can-i-find-the-zoom-api-token',
  fr: 'https://intercom.help/ava/fr/articles/3837352-ou-trouver-le-token-api-de-zoom',
};

const useStyles = makeStyles({
  connectButton: {
    color: 'white',
    display: 'flex',
    flexGrow: 1,
    fontWeight: 600,
    margin: 20,
    marginBottom: 10,
    marginTop: 0,
    textTransform: '',
  },
  dialog: {
    width: 600,
  },
  icon: {
    width: 100,
  },
  iconContainer: {
    display: 'flex',
    flexGrow: 1,
  },
  iconContainerClose: {
    borderRadius: 80,
    color: 'white',
    fontSize: 70,
    fontWeight: 800,
    height: 120,
    lineHeight: '120px',
    margin: 'auto',
    marginBottom: 10,
    textAlign: 'center',
    width: 120,
  },
  invitation: {
    display: 'flex',
  },
  progress: {
    height: 10,
    position: 'absolute',
    width: 10,
  },
  title: {
    fontSize: '2rem',
    fontWeight: '600',
    textAlign: 'center',
  },
  whereLink: {
    color: '#337ab7',
    marginBottom: 10,
    textAlign: 'center',
    textDecoration: 'underline',
  },
  tokenWarningDialog: {
    maxWidth: 580,
    margin: '0 auto',
  },
  tokenWarningImage: {
    display: 'block',
    height: 'auto',
    margin: '12px auto 24px',
    width: 180,
  },
  tokenWarningTitle: {
    fontWeight: 700,
  },
  tokenWarningDialogActions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  tokenWarningButton: theme => ({
    '&:hover': {
      background: theme.palette.ava.blue,
      opacity: 0.9,
    },
    background: theme.palette.ava.blue,
    color: theme.palette.background.light,
    fontSize: '1.2em',
    fontWeight: 700,
    margin: '0 auto 16px',
    padding: '0.4em 2.4em',
    textTransform: 'none',
    width: '100%',
  }),
});

interface IProps {
  broadcastToZoomToken?: string;
  createNewConversation: Function;
  i18n: Object;
  on: boolean;
  setOff: Function;
  speechLang: string;
  status: Object;
  t: Function;
  theme: Object;
  updateBroadcastToZoomToken: Function;
  user: Object;
}

export default function ZoomBroadcastCaptionsPopup(props: IProps) {
  const [apiToken, setApiToken] = React.useState('');
  const [showDuplicateTokenWarning, setShowDuplicateTokenWarning] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const updateApiToken = token => {
    const trimmedToken = token.replace(/\s|\n|\r|\r\n/g, '');
    setApiToken(trimmedToken);
  };

  const broadcastToZoom = async setOff => {
    setLoading(true);
    const { status, updateBroadcastToZoomToken, user } = props;

    updateBroadcastToZoomToken(apiToken);
    try {
      await conversations.exportCaptions({
        avaId: user.avaId,
        firebaseAuthUID: user.id,
        roomId: status.id,
        apiToken,
      });
      getSnack(props)(props.t('conversation.zoomExport.avaClosedCaptionsAddedInZoom'));
    } catch (err) {
      getSnack(props)(err.message, { variant: 'error' });
    } finally {
      setShowDuplicateTokenWarning(false);
      setLoading(false);
      setOff();
    }
  };

  const call = setOff => {
    if (apiToken === props.broadcastToZoomToken) {
      setShowDuplicateTokenWarning(true);
    } else {
      broadcastToZoom(setOff);
    }
  };

  const inputDisabled = loading;
  const sendDisabled = inputDisabled || apiToken.length < 10;

  const { theme, on, setOff, t, i18n } = props;

  const classes = useStyles(theme);

  return (
    <>
      <Dialog
        open={on && !showDuplicateTokenWarning}
        keepMounted
        onClose={setOff}
        aria-labelledby="form-dialog-title"
        classes={{ paper: classes.dialog }}
      >
        <DialogContent>
          <div className={classes.iconContainer}>
            <div
              className={classes.iconContainerClose}
              style={{
                backgroundColor: theme.palette.ava.blue,
              }}
            >
              CC
            </div>
          </div>
          <Typography className={classes.title}>{t('conversation.zoomExport.displayAvaCCinYourZoomCall')}</Typography>
          <Typography className={classes.whereLink} variant="h6">
            <a
              href={i18n.language === 'fr' ? intercomLinks.fr : intercomLinks.en}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('conversation.zoomExport.where?')}
            </a>
          </Typography>
          <TextField
            // label="Invitation"
            multiline
            autoFocus
            rows="3"
            value={apiToken}
            placeholder={t('conversation.zoomExport.placeholder')}
            disabled={inputDisabled}
            onChange={e => updateApiToken(e.target.value)}
            className={classes.invitation}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => call(setOff)}
            disabled={sendDisabled}
            className={classes.connectButton}
            style={{
              backgroundColor: theme.palette.ava.blue,
              opacity: sendDisabled ? 0.7 : 1,
            }}
          >
            {' '}
            {t('conversation.zoomExport.connect')}{' '}
            {loading && <CircularProgress size={20} className={classes.progress} />}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={on && showDuplicateTokenWarning}
        keepMounted
        onClose={() => {}}
        className={classes.tokenWarningDialog}
        PaperProps={{
          style: {
            padding: '12px 24px',
          },
        }}
      >
        <DialogContent>
          <img src={ZoomDuplicatedTokenImage} alt="Sad face" className={classes.tokenWarningImage} />
          <Typography variant="h4" className={classes.tokenWarningTitle} align="center" gutterBottom>
            {t('conversation.zoomExportDuplicateWarning.title')}
          </Typography>
          <Typography variant="body1" align="center" gutterBottom>
            {t('conversation.zoomExportDuplicateWarning.body1')}
          </Typography>
          <Typography variant="body1" align="center" gutterBottom>
            {t('conversation.zoomExportDuplicateWarning.body2')}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.tokenWarningDialogActions}>
          <Button onClick={() => broadcastToZoom(setOff)} className={classes.tokenWarningButton}>
            &nbsp;
            {!loading && t('conversation.zoomExportDuplicateWarning.confirm')}
            &nbsp;
            {loading && <CircularProgress size={20} color="secondary" className={classes.progress} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
